import React, { useState } from 'react';
import { Download, CheckCircle2, Mail, LayoutList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { homeworkService } from '../services/api';
import { Button, Card } from './ui';

function HomeworkCard({ homework }) {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const interactiveEnabled = process.env.REACT_APP_INTERACTIVE_HOMEWORK_ENABLED !== 'false';
  const showInteractiveCta = interactiveEnabled && ['active', 'overdue'].includes(homework.completionStatus);

  const statusLabel =
    homework.completionStatus === 'completed'
      ? 'Completed'
      : homework.completionStatus === 'overdue'
        ? 'Overdue'
        : homework.completionStatus === 'archived'
          ? 'Archived'
          : 'Active';

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await homeworkService.downloadHomework(homework._id);
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.error || 'Failed to download homework file.');
      }

      const fileName = `${(homework.title || 'homework').replace(/[^a-z0-9-_ ]/gi, '').trim() || 'homework'}.pdf`;
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const fallbackMessage = 'Failed to download homework. Please try again.';
      const responseData = error?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          alert(parsed?.error || fallbackMessage);
          return;
        } catch (_parseError) {
          // Ignore parse errors and use fallback message.
        }
      }

      alert(error?.message || fallbackMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailPdf = async () => {
    setIsEmailing(true);
    setEmailStatus(null);
    try {
      const response = await homeworkService.emailHomework(homework._id);
      setEmailStatus({ type: 'success', text: response.data.message || 'PDF sent to your email!' });
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to send email. Please try downloading instead.';
      setEmailStatus({ type: 'error', text: msg });
    } finally {
      setIsEmailing(false);
      setTimeout(() => setEmailStatus(null), 5000);
    }
  };

  return (
    <Card className="p-4">
      <div className="mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">{homework.title}</h3>
            {(homework.type || homework.topic) && (
              <p className="mt-1 text-xs uppercase tracking-wide text-neutral-500">{homework.type || homework.topic}</p>
            )}
            {homework.assignedAt && (
              <p className="mt-1 text-sm text-neutral-500">
                Assigned: {new Date(homework.assignedAt).toLocaleDateString()}
              </p>
            )}
            {homework.receivedAt && (
              <p className="mt-1 text-sm text-neutral-500">
                Received: {new Date(homework.receivedAt).toLocaleDateString()}
              </p>
            )}
            {homework.emailedAt && (
              <p className="mt-1 text-sm text-neutral-500">
                Emailed: {new Date(homework.emailedAt).toLocaleDateString()}
              </p>
            )}
            {homework.dueDate && (
              <p className="mt-1 text-sm text-neutral-500">
                Due: {new Date(homework.dueDate).toLocaleDateString()}
              </p>
            )}
            {homework.completedAt && (
              <p className="mt-1 text-sm text-neutral-500">
                Completed: {new Date(homework.completedAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
            homework.completionStatus === 'completed'
              ? 'bg-green-100 text-green-700'
              : homework.completionStatus === 'overdue'
                ? 'bg-red-100 text-red-700'
                : homework.completionStatus === 'archived'
                  ? 'bg-neutral-100 text-neutral-700'
                  : 'bg-blue-100 text-blue-700'
          }`}>
            {homework.completionStatus === 'completed' && <CheckCircle2 className="h-3 w-3" />}
            <span>{statusLabel}</span>
          </div>
        </div>

        {homework.clinicianNote && (
          <div className="mt-3 rounded-md bg-primary-50 p-3">
            <p className="text-sm text-primary-900">
              <strong>Note from your therapist:</strong> {homework.clinicianNote}
            </p>
          </div>
        )}

        {homework.description && (
          <p className="mt-2 text-sm text-neutral-600">{homework.description}</p>
        )}
      </div>

      {emailStatus && (
        <div className={`mb-3 rounded-md px-3 py-2 text-sm ${
          emailStatus.type === 'success'
            ? 'bg-green-50 text-green-700'
            : 'bg-red-50 text-red-700'
        }`}>
          {emailStatus.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {showInteractiveCta && (
          <Button
            variant="primary"
            onClick={() => navigate(`/homework/interactive/${homework._id}`)}
            className="flex-1 min-w-[160px]"
          >
            <LayoutList className="h-4 w-4" />
            Complete Interactive
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex-1 min-w-[160px]"
        >
          <Download className="h-4 w-4" />
          {isDownloading ? 'Downloading...' : 'Download Worksheet'}
        </Button>

        <Button
          variant="secondary"
          onClick={handleEmailPdf}
          disabled={isEmailing}
          className="flex-1 min-w-[160px]"
        >
          <Mail className="h-4 w-4" />
          {isEmailing ? 'Sending...' : 'Email PDF'}
        </Button>

      </div>
      {homework.receivedAt && !homework.completedAt && (
        <p className="mt-2 text-xs text-neutral-500">
          This assignment has been delivered to your dashboard.
        </p>
      )}
    </Card>
  );
}

export default HomeworkCard;
